import React, { useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

import { AuthenticationTabs, PasskeySettingsForm } from '../../../components';
import { Preloader, ToDashboardError } from '../../../components/ui';
import { useCurrentProject } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { isPasskeyAvailable } from '../../../utils';

const Passkey = ({ match, history }: RouteComponentProps): JSX.Element => {
  const { projectId } = match.params as RouteParams;
  const { currentProject, currentProjectFetching, error } = useCurrentProject(projectId);
  const { navigationPath } = usePaths();

  // SettingsContext lives on App and keeps the previously opened project until
  // fine_configuration resolves for this one, so currentProject on its own says
  // nothing about the id in the URL. Matching the id first is what stops the
  // effect below acting on stale data: without it, arriving here from a shadow
  // project redirects the user away from a project that is perfectly eligible,
  // because the first render still sees the old one.
  const isProjectLoaded = !!currentProject && currentProject.id === projectId;
  const isRedirecting = isProjectLoaded && !isPasskeyAvailable(currentProject);

  useEffect(() => {
    if (isRedirecting) {
      history.push({ pathname: navigationPath(projectId) });
    }
  }, [isRedirecting, history, navigationPath, projectId]);

  // Checked before the preloader. On a failed fetch currentProject never becomes
  // the project in the URL, so isProjectLoaded stays false and a preloader shown
  // first would spin forever instead of surfacing the error.
  if (error) {
    return <ToDashboardError error={error} pageName="passkey_open" />;
  }

  // The redirect runs in an effect, so without this the form would render for a
  // frame on a project that is not allowed to see it. isProjectLoaded also keeps
  // the preloader up while the previous project is still the one in context.
  if (currentProjectFetching || !isProjectLoaded || isRedirecting) {
    return (
      <AuthenticationTabs>
        <Preloader />
      </AuthenticationTabs>
    );
  }

  return (
    <AuthenticationTabs>
      <PasskeySettingsForm projectId={projectId} />
    </AuthenticationTabs>
  );
};

export default withRouter(Passkey);
